import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import { ControlType, SettingConfig, SettingValue } from '../../types/settingsTypes';
import { getCategoriesFromSettingsConfig } from '../../utils/settings/settingsHelpers';
import { mergeSettingsConfig } from '../../utils/settings/settingsConfigMerger';
import { SettingsInputs, SettingInputProps } from './SettingsInputs';
import { ModelImportModal } from '../ModelImportModal';
import { ReactComponent as LogoIcon } from '../../../../../assets/edgerunnerLogo.svg';
import { useToastStore } from '../../stores/ToastStore';
import { useUserSettings, useBackendOptions } from '../../stores/settingsRegistry';
import { useDocumentStore } from '../../stores/documentStore';
import { apiClient } from '../../clients/APIClient';
import { useChatListStore } from '../../stores/chatListStore';
import { useDocumentSelectionStore } from '../../stores/documentSelectionStore';
import { downloadMagazine, generateMagazineFilename } from '../../utils/magazineExport';
import { ConfirmDeleteModal } from '../common/ConfirmDeleteModal';

/** Maps control types to input components. Add new types here after adding to ControlType. */
const CONTROL_TYPE_COMPONENTS: Record<ControlType, React.ComponentType<SettingInputProps> | null> = {
	text: SettingsInputs.Text,
	select: SettingsInputs.Select,
	multiSelect: SettingsInputs.MultiSelect,
	switch: SettingsInputs.Switch,
	checkbox: SettingsInputs.Checkbox,
	multiCheckbox: SettingsInputs.MultiCheckboxGrouped,
	directoryPicker: SettingsInputs.DirectoryPicker,
	secureInput: SettingsInputs.SecureInput,
	dualInput: SettingsInputs.DualInput,
	numberInput: SettingsInputs.NumberInput,
	textbox: SettingsInputs.ListTextbox,
	info: SettingsInputs.Info,
	infoAction: SettingsInputs.InfoAction,
};

type StoreActions = {
	update: (key: string, value: SettingValue) => Promise<void>;
	reset: (key: string) => Promise<void>;
};

interface ModalSettingsProps {
	closeSettings: () => void;
}

interface SettingProps {
	settingDef: SettingConfig;
	devModeActive: boolean;
	helpers: {
		getValue: (key: string) => SettingValue;
		getConfig: (key: string) => SettingConfig | undefined;
	};
	actions: StoreActions;
	onAction?: () => void;
	actionLoading?: boolean;
}

const EXPORT_ALL_SETTING: SettingConfig = {
	key: 'action.exportAll',
	label: 'Export & Wipe',
	description: 'Download a full backup of all collections, files, and chat histories as a magazine (.mag) file, then optionally wipe storage',
	category: 'Export',
	controlType: 'infoAction',
	type: 'string',
	default: '',
	editable: true,
	actionLabel: 'Export All',
	actionIcon: 'fas fa-download',
	loadingLabel: 'Exporting...',
};

function Setting({
	settingDef,
	devModeActive = false,
	helpers,
	actions,
	onAction,
	actionLoading,
}: SettingProps) {
	const [isResetting, setIsResetting] = useState(false);

	const currentValue = helpers.getValue(settingDef.key);

	const changeHandler = useCallback(
		(value: SettingValue) => actions.update(settingDef.key, value),
		[actions, settingDef.key],
	);

	const resetHandler = useCallback(async () => {
		setIsResetting(true);
		try {
			await actions.reset(settingDef.key);
		} finally {
			setIsResetting(false);
		}
	}, [actions, settingDef.key]);

	// Non-editable backend options render as read-only Info — the backend rejects
	// updates so the declared control would invite edits that silently revert.
	// Client configs are exempt in dev mode: they persist locally with no backend
	// to reject them, so dev mode keeps them fully interactive (incl. action buttons).
	const renderAsInfo = !settingDef.editable && !(devModeActive && settingDef.isClientConfig);
	const effectiveControlType: ControlType = renderAsInfo ? 'info' : settingDef.controlType;
	// Reset is meaningless for info displays (no value to revert).
	const showReset = settingDef.showReset && !renderAsInfo;

	const renderSettingInput = () => {
		const InputComponent = CONTROL_TYPE_COMPONENTS[effectiveControlType];
		if (!InputComponent) return null;

		// Control-type-specific props
		if (effectiveControlType === 'text' && 'tokenFlag' in settingDef) {
			return (
				<InputComponent
					setting={settingDef}
					value={currentValue}
					onChange={changeHandler}
					devModeActive={devModeActive}
					tokenFlag={settingDef.tokenFlag}
				/>
			);
		}

		if (effectiveControlType === 'info') {
			return (
				<InputComponent
					setting={settingDef}
					value={currentValue}
				/>
			);
		}

		if (effectiveControlType === 'infoAction') {
			return (
				<InputComponent
					setting={settingDef}
					value={actionLoading ?? false}
					onChange={() => onAction?.()}
				/>
			);
		}

		return (
			<InputComponent
				setting={settingDef}
				value={currentValue}
				onChange={changeHandler}
				devModeActive={devModeActive}
			/>
		);
	};

	const stacked = settingDef.layout === 'stacked';

	return (
		<div
			className={`settings-item ${stacked ? 'flex-col items-start' : ''} ${!(devModeActive || settingDef.editable) ? 'settings-item-disabled' : ''} ${isResetting ? 'settings-item-loading' : ''}`}
		>
			<div className={`settings-item-content ${stacked ? 'w-full' : ''}`}>
				<div className={stacked ? 'flex items-center justify-between' : ''}>
					<div className="settings-item-label">
						<div className="settings-item-title">
							{settingDef.label}
						</div>
						{settingDef.requiresRestart && (
							<span className="settings-item-badge">
								Requires restart
							</span>
						)}
					</div>
					{stacked && showReset && (
						<SettingsInputs.ResetButton onClick={resetHandler} />
					)}
				</div>
				{settingDef.description && (
					<p className="settings-item-description">
						{settingDef.description}
					</p>
				)}
			</div>

			<div className={`settings-item-controls ${stacked ? 'w-full flex-col items-stretch' : ''}`}>
				{!stacked && showReset && (
					<SettingsInputs.ResetButton onClick={resetHandler} />
				)}
				{renderSettingInput()}
			</div>
		</div>
	);
}

/** Prerequisite: registerSettingsStores() must be called in platform App.tsx */
export function ModalSettings({ closeSettings }: ModalSettingsProps) {
	const { setModalOpen, showToast } = useToastStore();
	const updateDocumentContext = useDocumentStore((s) => s.updateDocumentContext);
	const userSettings = useUserSettings();
	const backendOptions = useBackendOptions(); // undefined for web

	const [activeTab, setActiveTab] = useState('general');
	const [showModelImport, setShowModelImport] = useState(false);
	// Only superusers (web/appliance admins) see the model-import entry; the
	// backend enforces this too, this just hides it for everyone else.
	const [isAdmin, setIsAdmin] = useState(false);
	useEffect(() => {
		let cancelled = false;
		apiClient.auth
			.getCurrentUser()
			.then((u) => {
				if (!cancelled) setIsAdmin(!!u?.is_superuser);
			})
			.catch(() => {
				if (!cancelled) setIsAdmin(false);
			});
		return () => {
			cancelled = true;
		};
	}, []);
	const [processedSettingsConfig, setProcessedSettingsConfig] = useState<
		Record<string, SettingConfig[]>
	>({});
	const [tabCategories, setTabCategories] = useState<string[]>([]);
	const [uiIsLoading, setUiIsLoading] = useState(true);
	const [devModeActive, setDevModeActive] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const [showWipeModal, setShowWipeModal] = useState(false);
	const wipeIdsRef = useRef<{ collectionIds: string[]; fileIds: string[]; historyIds: string[] } | null>(null);

	const exportAllDump = useCallback(async () => {
		setIsExporting(true);
		try {
			const [collections, files, histories] = await Promise.all([
				apiClient.collections.getCollections(),
				apiClient.files.listFiles(),
				apiClient.history.listHistory(),
			]);
			const result = await apiClient.files.exportMagazine({
				collection_ids: collections.map((c) => c.id),
				file_ids: files.map((f) => f.id),
				history_ids: histories.map((h) => h.id),
				stream: true,
			});
			downloadMagazine(
				result instanceof Blob ? result : result.b64_mag,
				generateMagazineFilename('dump'),
			);
			wipeIdsRef.current = {
				collectionIds: collections.map((c) => c.id),
				fileIds: files.map((f) => f.id),
				historyIds: histories.map((h) => h.id),
			};
			setTimeout(() => setShowWipeModal(true), 1500);
		} catch {
			showToast('Export failed', 'error', { timeout: 3000 });
		} finally {
			setIsExporting(false);
		}
	}, [showToast]);

	const handleWipeConfirm = useCallback(async () => {
		const ids = wipeIdsRef.current;
		if (!ids) return;
		await Promise.allSettled([
			...ids.collectionIds.map((id) => apiClient.collections.deleteCollectionById(id)),
			...ids.fileIds.map((id) => apiClient.files.deleteFileById(id)),
			...ids.historyIds.map((id) => apiClient.history.deleteHistoryById(id)),
		]);
		wipeIdsRef.current = null;
		setShowWipeModal(false);
		useDocumentSelectionStore.getState().clearAllSelections();
		useChatListStore.getState().onNewChat();
		await useChatListStore.getState().refreshChats();
		await updateDocumentContext({ operation: 'refresh', shouldRefreshServer: true });
		showToast('Storage wiped', 'success', { timeout: 3000 });
	}, [showToast, updateDocumentContext]);

	// Aggregate loading/error state from both stores
	const dataLoading = userSettings.isLoading || (backendOptions?.isLoading ?? false);
	const errors = useMemo(() => ({
		user: userSettings.error,
		backend: backendOptions?.error,
	}), [userSettings.error, backendOptions?.error]);

	// SettingsModal owns the merge logic - combines configs from both stores
	const allConfigs = useMemo(() => {
		const uConfigs = userSettings.helpers.getAllConfigs();
		const bConfigs = backendOptions?.helpers.getAllConfigs() ?? {};
		return mergeSettingsConfig(uConfigs, bConfigs);
	}, [userSettings.helpers, backendOptions?.helpers]);

	// SettingsModal owns the routing logic - routes to correct store based on isClientConfig
	const helpers = useMemo(() => ({
		getValue: (key: string): SettingValue => {
			const config = userSettings.helpers.getConfig(key)
				|| backendOptions?.helpers.getConfig(key);
			if (config?.isClientConfig) {
				return userSettings.helpers.getValue(key);
			}
			return backendOptions?.helpers.getValue(key) ?? '';
		},
		getConfig: (key: string): SettingConfig | undefined => {
			return userSettings.helpers.getConfig(key)
				|| backendOptions?.helpers.getConfig(key);
		},
	}), [userSettings.helpers, backendOptions?.helpers]);

	const actions = useMemo(() => ({
		update: async (key: string, value: SettingValue): Promise<void> => {
			const config = helpers.getConfig(key);
			if (config?.isClientConfig) {
				await userSettings.actions.update(key, value);
			} else {
				await backendOptions?.actions.update(key, value);
			}
		},
		reset: async (key: string): Promise<void> => {
			const config = helpers.getConfig(key);
			if (config?.isClientConfig) {
				await userSettings.actions.reset(key);
			} else {
				await backendOptions?.actions.reset(key);
			}
		},
	}), [helpers, userSettings.actions, backendOptions?.actions]);

	useEffect(() => {
		setModalOpen(true);
		return () => setModalOpen(false);
	}, [setModalOpen]);

	useEffect(() => {
		if (!dataLoading) {
			setUiIsLoading(true);
			setProcessedSettingsConfig(allConfigs);
			setUiIsLoading(false);
		}
	}, [
		dataLoading,
		allConfigs,
		userSettings.settings,
		backendOptions?.options,
		errors.user,
		errors.backend,
	]);

	// Re-filter categories when devModeActive changes or config is updated
	useEffect(() => {
		if (Object.keys(processedSettingsConfig).length > 0) {
			const allCategories = getCategoriesFromSettingsConfig(processedSettingsConfig)
				.filter((category) => category.toLowerCase() !== 'tools');

			// Filter out categories that have no visible settings based on devModeActive
			const visibleCategories = allCategories.filter((category) => {
				const settingsInCategory = processedSettingsConfig[category] || [];
				return settingsInCategory.some(
					(setting) => devModeActive || setting.editable
				);
			});

			setTabCategories(visibleCategories);
		}
	}, [processedSettingsConfig, devModeActive]);

	useEffect(() => {
		if (tabCategories.length > 0 && (!activeTab || !tabCategories.includes(activeTab))) {
			setActiveTab(tabCategories[0]);
		} else if (tabCategories.length === 0 && activeTab) {
			setActiveTab('');
		}
	}, [tabCategories, activeTab]);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				if (closeSettings) {
					closeSettings();
				}
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [closeSettings]);

	if (errors.user || errors.backend) {
		return (
			<div className="fixed top-0 left-0 right-0 bottom-0 bg-neutral-700/50 flex justify-center items-center z-[1000]">
				<div className="bg-neutral-700 p-lg pb-xs pr-0 rounded-medium w-[90%] max-w-[1400px] h-[85%] max-h-[800px] shadow-large border-2 border-neutral-400 flex flex-col box-border">
					<div className="flex justify-between items-center pb-xs pr-lg mb-xs">
						<h2 className="m-0 text-lg text-neutral-100">
							Error Loading Settings
						</h2>
						<button
							onClick={closeSettings}
							className="bg-transparent border-0 text-xl cursor-pointer text-neutral-100 hover:text-red-400 transition-colors duration-200"
							type="button"
							aria-label="Close settings"
						>
							<i className="fas fa-times" />
						</button>
					</div>
					<div className="flex-grow mb-xs text-neutral-100 flex flex-col">
						{errors.user && (
							<p className="text-status-error mb-md">
								User settings error: {errors.user}
							</p>
						)}
						{errors.backend && (
							<p className="text-status-error mb-md">
								Backend options error: {errors.backend}
							</p>
						)}
						<button
							onClick={() => window.location.reload()}
							className="bg-red-500 text-neutral-white px-md py-sm rounded-medium text-md font-medium border-0 cursor-pointer transition-all duration-200 hover:bg-red-600 active:bg-red-700"
							type="button"
							aria-label="Reload settings"
						>
							Try Reloading
						</button>
					</div>
				</div>
			</div>
		);
	}

	const renderSettingsTab = (categoryKey: string) => {
		const settingsGroup = processedSettingsConfig[categoryKey];
		if (!settingsGroup) {
			return (
				<div className="text-neutral-200">
					Settings for category {categoryKey} are not available.
				</div>
			);
		}

		const filteredSettingsGroup = settingsGroup.filter(
			(setting) => devModeActive || setting.editable,
		);

		return (
			<div className="w-full flex flex-col h-full overflow-y-auto pr-xxs">
				{filteredSettingsGroup.map((settingDef) => (
					<Setting
						key={`${settingDef.key}-${categoryKey}`}
						settingDef={settingDef}
						devModeActive={devModeActive}
						helpers={helpers}
						actions={actions}
					/>
				))}
				{categoryKey === 'Export' && (
					<Setting
						key="action.exportAll"
						settingDef={EXPORT_ALL_SETTING}
						devModeActive={devModeActive}
						helpers={helpers}
						actions={actions}
						onAction={exportAllDump}
						actionLoading={isExporting}
					/>
				)}
			</div>
		);
	};

	const renderCategories = () => {
		const categoryButtons =
			tabCategories.length > 0
				? tabCategories.map((categoryName) => (
						<button
							key={categoryName}
							className={`sidebar-item ${
								activeTab === categoryName ? 'sidebar-item-active' : ''
							}`}
							onClick={() => setActiveTab(categoryName)}
							type="button"
						>
							{categoryName.charAt(0).toUpperCase() +
								categoryName.slice(1)}
						</button>
				  ))
				: uiIsLoading
				? [<p key="__loading" className="text-neutral-200">Loading categories...</p>]
				: [];
		return (
			<>
				{categoryButtons}
				{/* Admin-only model import (backend also enforces admin). */}
				{isAdmin && (
					<button
						key="__models"
						className="sidebar-item"
						onClick={() => setShowModelImport(true)}
						type="button"
					>
						Models
					</button>
				)}
			</>
		);
	};

	const renderContent = () => {
		if (activeTab && processedSettingsConfig[activeTab]) {
			return renderSettingsTab(activeTab);
		}
		if (uiIsLoading) {
			return <div className="text-neutral-200">Loading content...</div>;
		}
		return <div className="text-neutral-200">Select a category.</div>;
	};

	return (
		<>
		<div
			className="fixed top-0 left-0 right-0 bottom-0 bg-neutral-700/50 flex justify-center items-center z-[1000]"
			data-testid="settings-modal"
		>
			<div className="bg-neutral-700 p-lg pb-xs pr-0 rounded-medium w-[90%] max-w-[1400px] h-[85%] max-h-[800px] shadow-large border-2 border-neutral-400 flex flex-col box-border">
				<div className="flex justify-between items-center pb-xs pr-lg mb-xs">
					<h2 className="m-0 text-lg text-neutral-100">Settings</h2>
					<button
						onClick={closeSettings}
						className="bg-transparent border-0 text-xl cursor-pointer text-neutral-100 hover:text-red-400 transition-colors duration-200"
						type="button"
						aria-label="Close settings"
					>
						<i className="fas fa-times" />
					</button>
				</div>
				<div className="flex-grow mb-xs text-neutral-100 flex flex-row justify-start h-full overflow-y-auto">
					<div className={`border-r ${devModeActive ? 'border-red-400' : 'border-neutral-400'} flex flex-col justify-between h-full w-1/5 relative left-0 top-0 bottom-lg`}>
						<div className="w-full h-full flex flex-col pr-xs box-border">
							{renderCategories()}
						</div>
						<div className="flex justify-between items-center">
							<button
								type="button"
								className="bg-transparent border-0 pl-xs pb-xs cursor-pointer"
								aria-label="Toggle dev mode"
								onClick={() => setDevModeActive(!devModeActive)}
							>
								<LogoIcon
									className={`w-6 h-6 transition-[filter,color] duration-200 ${
										devModeActive
											? 'text-red-500 [filter:drop-shadow(0_0_3px_rgba(239,68,68,0.7))_drop-shadow(0_0_8px_rgba(239,68,68,0.3))]'
											: 'text-neutral-300 hover:text-red-400 hover:drop-shadow-[0_0_3px_rgba(239,68,68,0.35)]'
									}`}
								/>
							</button>
						</div>
					</div>
					<div className="flex flex-col bg-neutral-700 ml-xs pr-xxs flex-1 min-w-0 box-border">
						{renderContent()}
					</div>
				</div>
			</div>
		</div>
		{isAdmin && showModelImport && (
			<ModelImportModal onClose={() => setShowModelImport(false)} />
		)}
		<ConfirmDeleteModal
			isOpen={showWipeModal}
			itemLabel="all storage"
			onConfirm={handleWipeConfirm}
			onCancel={() => setShowWipeModal(false)}
			details={
				<p>
					Verify your magazine file has downloaded before continuing - this will
					permanently delete all collections, files, and chat histories from storage.
				</p>
			}
		/>
		</>
	);
}
